/**
 * Ace Editor 统一样式配置
 * 所有使用 Ace Editor 的地方引用此文件，统一管理样式
 */
/* eslint-disable theme-colors/no-literal-colors */
import { css, Global } from '@emotion/react';
import React from 'react';
import { styled } from '@superset-ui/core';

// 编辑器主题配置
export const EDITOR_THEME = {
  dark: 'tomorrow_night',
  light: 'github',
} as const;

// 默认使用浅色主题
export const DEFAULT_THEME = EDITOR_THEME.light;

// 字体配置
export const EDITOR_FONT = {
  size: '13px',
  family: '"Cascadia Code", "Fira Code", Consolas, Menlo, monospace',
} as const;

// 现代化浅蓝主题配色 — 对齐 Superset #4755ab 品牌色
const C = {
  bg: '#fafbfc',
  gutterBg: '#f0f2f5',
  gutterText: '#8b929a',
  text: '#24292e',
  keyword: '#4755ab',
  string: '#d63384',
  number: '#0d6efd',
  comment: '#6c757d',
  fn: '#6f42c1',
  variable: '#24292e',
  cursor: '#24292e',
  activeLine: '#f0f2f5',
  selection: 'rgba(71,85,171,0.15)',
  line: '#e9ecef',
  highlight: '#fff8c5',
};

// 全局样式 CSS — 现代化浅蓝风格
export const editorGlobalStyles = css`
  .ace_editor,
  .ace_text-input,
  .ace_line {
    font-size: ${EDITOR_FONT.size} !important;
    font-family: ${EDITOR_FONT.family} !important;
  }

  /* 编辑器主体 */
  .ace_editor {
    background-color: ${C.bg} !important;
    color: ${C.text} !important;
  }

  /* 仅在有边框外壳时去掉内层 ace 边框，避免与外壳双层叠加 */
  [data-ace-frame] .ace_editor {
    border: none !important;
    border-radius: 0 !important;
  }
  .ace_scroller {
    background-color: ${C.bg} !important;
  }
  .ace_text-layer {
    width: 100% !important;
  }

  /* 行号列 */
  .ace_gutter {
    background-color: ${C.gutterBg} !important;
    color: ${C.gutterText} !important;
  }
  .ace_gutter-layer {
    background-color: ${C.gutterBg} !important;
  }
  .ace_gutter-cell {
    color: ${C.gutterText} !important;
  }
  .ace_gutter-active-line {
    background-color: ${C.gutterBg} !important;
    color: ${C.gutterText} !important;
  }

  /* 光标 */
  .ace_cursor {
    color: ${C.cursor} !important;
    border-left-color: ${C.cursor} !important;
    width: 2px !important;
  }

  /* 行/选中 */
  .ace_marker-layer .ace_active-line {
    background: ${C.activeLine} !important;
  }
  .ace_marker-layer .ace_selection {
    background: ${C.selection} !important;
  }

  /* 语法高亮 */
  .ace_keyword {
    color: ${C.keyword} !important;
    font-weight: 600 !important;
  }
  .ace_identifier {
    color: ${C.variable} !important;
  }
  .ace_comment {
    color: ${C.comment} !important;
    font-style: italic !important;
  }
  .ace_string,
  .ace_constant,
  .ace_string2 {
    color: ${C.string} !important;
  }
  .ace_numeric {
    color: ${C.number} !important;
  }
  .ace_function,
  .ace_support.function {
    color: ${C.fn} !important;
  }
  .ace_variable {
    color: ${C.variable} !important;
  }
  .ace_storage {
    color: ${C.keyword} !important;
    font-weight: 600 !important;
  }
  .ace_support {
    color: ${C.keyword} !important;
  }
  .ace_operator,
  .ace_punctuation {
    color: ${C.text} !important;
  }
  .ace_paren,
  .ace_rparen,
  .ace_lparen {
    color: ${C.text} !important;
  }
  .ace_text {
    color: ${C.text} !important;
  }
  .ace_line {
    color: ${C.text} !important;
  }
  .ace_line_group {
    color: ${C.text} !important;
  }
  .ace_indent-guide {
    color: ${C.line} !important;
  }

  /* 搜索高亮 */
  .ace_marker-layer .ace_selected-word {
    background: ${C.highlight} !important;
    border-color: #e6c345 !important;
  }

  /* 自动补全弹出框 */
  .ace_autocomplete {
    background: #fff !important;
    border: 1px solid #d0d7de !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
    color: ${C.text} !important;
    font-family: ${EDITOR_FONT.family} !important;
    font-size: ${EDITOR_FONT.size} !important;
  }
  .ace_autocomplete .ace_line {
    color: ${C.text} !important;
    background: transparent !important;
  }
  .ace_autocomplete .ace_completion-highlight {
    color: ${C.keyword} !important;
    font-weight: 600;
  }
  .ace_autocomplete .ace_rightAlignedText {
    color: ${C.gutterText} !important;
  }
  .ace_autocomplete .ace_selected {
    background: ${C.selection} !important;
    color: ${C.text} !important;
  }
  .ace_autocomplete .ace_selected .ace_completion-highlight {
    color: ${C.keyword} !important;
  }
  .ace_autocomplete .ace_constant,
  .ace_autocomplete .ace_keyword {
    color: ${C.keyword} !important;
  }
  .ace_autocomplete .ace_function {
    color: ${C.fn} !important;
  }
  .ace_autocomplete .ace_variable {
    color: ${C.text} !important;
  }
  .ace_autocomplete .ace_string {
    color: ${C.string} !important;
  }
  .ace_autocomplete .ace_numeric {
    color: ${C.number} !important;
  }
  .ace_autocomplete::-webkit-scrollbar {
    width: 8px;
  }
  .ace_autocomplete::-webkit-scrollbar-track {
    background: ${C.gutterBg};
  }
  .ace_autocomplete::-webkit-scrollbar-thumb {
    background: #d0d7de;
    border-radius: 4px;
  }
  .ace_autocomplete::-webkit-scrollbar-thumb:hover {
    background: ${C.gutterText};
  }
`;

// 全局样式组件 - 可以直接在 JSX 中使用
export const EditorGlobalStyles: React.FC = () => (
  <Global styles={editorGlobalStyles} />
);

type AceEditorFrameStyleProps = {
  $height?: number | string;
  $minHeight?: number | string;
  /** 在 flex 父级内撑满（SQL Lab 主编辑区等） */
  $flex?: boolean;
};

const AceEditorFrameStyled = styled.div<AceEditorFrameStyleProps>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.grayscale.light2};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.grayscale.light5};

  ${({ $height }) =>
    $height != null &&
    css`
      height: ${typeof $height === 'number' ? `${$height}px` : $height};
      min-height: ${typeof $height === 'number' ? `${$height}px` : $height};
    `}

  ${({ $minHeight }) =>
    $minHeight != null &&
    css`
      min-height: ${typeof $minHeight === 'number'
        ? `${$minHeight}px`
        : $minHeight};
    `}

  ${({ $flex }) =>
    $flex &&
    css`
      flex: 1 1 auto;
      min-height: 0;
      height: 100%;
    `}

  > div {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    width: 100%;
    height: 100%;
  }

  .ace_editor {
    flex: 1 1 auto;
    min-height: 0;
    width: 100% !important;
    height: 100% !important;
  }
`;

/**
 * 通用 Ace 边框外壳（P0/P1 无自带边框的编辑器专用）。
 * 已有外壳的链路勿替换：TextAreaControl wrapper、ModalAceEditorShell、StyledJsonEditor 等。
 */
export const AceEditorFrame = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof AceEditorFrameStyled>
>(function AceEditorFrame({ className, ...props }, ref) {
  return (
    <AceEditorFrameStyled
      ref={ref}
      data-ace-frame=""
      className={className}
      {...props}
    />
  );
});

/** 模态框内 SQL/JSON 等 Ace 编辑器外壳（固定高度 + flex 撑满） */
const ModalAceEditorShellStyled = styled.div<{ $height?: number | string }>`
  height: ${({ $height, theme }) => {
    if ($height == null) {
      return `${theme.gridUnit * 75}px`;
    }
    return typeof $height === 'number' ? `${$height}px` : $height;
  }};
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.grayscale.light3};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  overflow: hidden;

  > div {
    flex: 1;
    min-height: 0;
    height: 100%;
  }
`;

export const ModalAceEditorShell = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof ModalAceEditorShellStyled>
>(function ModalAceEditorShell({ className, ...props }, ref) {
  return (
    <ModalAceEditorShellStyled
      ref={ref}
      data-ace-frame=""
      className={className}
      {...props}
    />
  );
});

export default editorGlobalStyles;
